import * as React from 'react';
import { Box, TableRow, Table, TableHead, TableCell, TableBody, styled } from '@mui/material';
import { AppCells } from './AppCells';
import LoadingScreen from 'src/frontend-common/components/LoadingScreen';
import FailureScreen from 'src/frontend-common/components/FailureScreen';
import EmptyState from 'src/frontend-common/components/EmptyState';
import { App } from 'src/dto/App';
import { AppCodeModal } from 'src/pages/Apps/components/AppCodeModal';
import { selectUser } from 'src/redux/slices/userSlice';
import { useSelector } from 'react-redux';
import { isAdmin } from 'src/utils/user';

type Props =
  | { state: 'loading' | 'error' }
  | {
      state: 'ready';
      apps: App[];
      onEdit: (app: App) => void;
    };

const CustomLoadingScreen = styled(LoadingScreen)(({ theme }) => ({
  minHeight: '500px',
  backgroundColor: theme.palette.background.paper,
}));

const Wrapper = styled(Box)(() => ({
  overflowY: 'scroll',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
}));

export const AppsList: React.FC<Props> = (props) => {
  const [modal, setModal] = React.useState<App | null>(null);
  const noApps = props.state === 'ready' && props.apps.length === 0;
  const user = useSelector(selectUser);
  const isUserAdmin = isAdmin(user);
  if (noApps) {
    return (
      <Box style={{ position: 'relative' }}>
        <EmptyState style={{ width: '100%', padding: '80px 20px' }} />
      </Box>
    );
  }

  return (
    <Wrapper data-cy="apps-list">
      <AppCodeModal open={modal !== null} onClose={() => setModal(null)} appKey={modal ? modal.js_app_key : ''} />

      {props.state === 'loading' && <CustomLoadingScreen variant="circle" />}

      <Table>
        {props.state === 'ready' && (
          <>
            <TableHead data-cy="apps-list">
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>API key</TableCell>
                <TableCell>JS key</TableCell>
                <TableCell>Status</TableCell>
                {isUserAdmin && <TableCell>Allow Negative</TableCell>}
                {isUserAdmin && <TableCell>Max Enrichment Time</TableCell>}
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {props.apps.map((a) => (
                <TableRow key={a.app_key} hover>
                  <AppCells app={a} onViewSnippet={() => setModal(a)} onEdit={props.onEdit} />
                </TableRow>
              ))}
            </TableBody>
          </>
        )}
      </Table>

      {props.state === 'error' && <FailureScreen>there was an error when fetching results!</FailureScreen>}
    </Wrapper>
  );
};
